// src/pages/videos/VideosPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Spinner, Badge } from "react-bootstrap";
import { BsYoutube, BsSearch, BsLightningCharge } from "react-icons/bs";
import { FaPodcast } from "react-icons/fa";

import { db } from "../../shared/libs/firebaseConfig";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";

import { bumpVideoView } from "../../shared/utils/bumpVideoView";

import "../../shared/styles/VideoUpdate.css"; // มี .yt-play-btn, .yt-play-btn--podcast ถ้าคุณใส่ไว้แล้ว
// ถ้าคุณมีไฟล์ CSS ของหน้า videos แยกก็ import เพิ่มได้

// ----------------------
// helpers
// ----------------------
function toDateSafe(v) {
  try {
    if (v?.toDate) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") return new Date(v);
  } catch {}
  return null;
}

function getYouTubeId(url = "") {
  try {
    const u = new URL(url);
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    if (u.hostname.includes("youtu.be")) return u.pathname.replace("/", "") || null;
    if (u.pathname.startsWith("/embed/")) return u.pathname.split("/embed/")[1]?.split("?")[0] || null;

    if (u.pathname.includes("/shorts/")) {
      return u.pathname.split("/shorts/")[1]?.split("?")[0] || null;
    }

    return null;
  } catch {
    return null;
  }
}

function ytEmbedUrl(videoId) {
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
}

const CATEGORY_OPTIONS = [
  "ทั้งหมด",
  "เนติบัณฑิต",
  "ทนายความ",
  "อัยการ",
  "ผู้พิพากษา",
  "สอบ ก.พ.",
  "ก.พ.",
];

function normalizeKind(k) {
  const v = String(k || "all").toLowerCase();
  if (v === "youtube") return "youtube";
  if (v === "appetizer") return "appetizer";
  if (v === "oral_exam_tips") return "oral_exam_tips";
  if (v === "podcast") return "podcast";
  if (v === "shorts") return "shorts";
  if (v === "shorts_prepare_for_exams") return "shorts_prepare_for_exams";
  return "all";
}

function normalizeSort(s) {
  const v = String(s || "latest").toLowerCase();
  if (v === "views") return "views";
  return "latest";
}

function VideoCard({ v }) {
  const [playing, setPlaying] = useState(false);

  const href = v.url || v.fileUrl || "#";
  const isYouTubeLike =
    v.kind === "youtube" ||
    v.kind === "appetizer" ||
    v.kind === "oral_exam_tips" ||
    v.kind === "podcast" ||
    v.kind === "shorts" ||
    v.kind === "shorts_prepare_for_exams";

  const isShorts =
    v.kind === "shorts" ||
    v.kind === "shorts_prepare_for_exams";
  const videoId = isYouTubeLike ? getYouTubeId(href) : null;

  const d = toDateSafe(v.updatedAt) || toDateSafe(v.createdAt);
  const dateText = d
    ? d.toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" })
    : "";

  return (
    <div className="card app-card h-100">
      <div className="ratio ratio-16x9 bg-surface position-relative">
        {isYouTubeLike && videoId && playing ? (
          <iframe
            src={ytEmbedUrl(videoId)}
            title={v.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ border: 0 }}
          />
        ) : (
          <>
            <img
              src={v.thumbUrl || (videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "")}
              alt={v.title || ""}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />

            {isYouTubeLike && videoId ? (
              <button
                type="button"
                className={`video-play-btn ${
                  isShorts
                    ? "video-play-btn--shorts"
                    : v.kind === "podcast"
                    ? "video-play-btn--podcast"
                    : "video-play-btn--youtube"
                }`}
                aria-label="เล่นวิดีโอ"
                onClick={async () => {
                  setPlaying(true);
                  try { await bumpVideoView(v.id); } catch {}
                }}
              >
                {isShorts ? (
                  <BsLightningCharge size={54} className="video-play-icon" />
                ) : v.kind === "podcast" ? (
                  <FaPodcast size={54} className="video-play-icon" />
                ) : (
                  <BsYoutube size={54} className="video-play-icon" />
                )}
              </button>
            ) : (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="stretched-link"
                aria-label="เปิดลิงก์"
              />
            )}
          </>
        )}
      </div>

      <div className="card-body">
        <div className="fw-semibold" style={{ lineHeight: 1.25 }}>
          {v.title || "-"}
        </div>

        {/* {v.description ? (
          <div
            className="text-muted small mt-1"
            style={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              lineHeight: 1.35,
            }}
          >
            {v.description}
          </div>
        ) : null} */}

        <div className="text-secondary small mt-2">
          {dateText}
          {Array.isArray(v.categories) && v.categories.length > 0 ? <> • {v.categories[0]}</> : null}
          {typeof v.viewCounts === "number" ? <> • {v.viewCounts.toLocaleString()} views</> : null}
        </div>

        {isYouTubeLike && href && (
          <div className="mt-2">
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="small text-decoration-none app-link"
            >
              เปิดใน{" "}
                {v.kind === "podcast"
                  ? "Podcast"
                  : isShorts
                  ? "Shorts"
                  : "YouTube"}{" "}
                ›
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default function VideosPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // url params
  const kind = useMemo(() => normalizeKind(searchParams.get("kind")), [searchParams]);
  const sort = useMemo(() => normalizeSort(searchParams.get("sort")), [searchParams]);
  const catParam = searchParams.get("cat") || "ทั้งหมด";
  const cat = useMemo(() => (catParam && catParam !== "all" ? catParam : "ทั้งหมด"), [catParam]);

  // UI search box (optional)
  const qParam = searchParams.get("q") || "";
  const [queryText, setQueryText] = useState(qParam);

  // data state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const lastDocRef = useRef(null);
  const exhaustedRef = useRef(false);
  const reqIdRef = useRef(0);

  const pageSize = 18;

  const titleNode = useMemo(() => {
    if (kind === "youtube") {
      return (
        <>
          <BsYoutube style={{ marginRight: 6 }} /> YouTube
        </>
      );
    }

    if (kind === "appetizer") {
      return (
        <>
          <BsYoutube style={{ marginRight: 6 }} /> ปูพื้นฐานเรียกน้ำย่อย
        </>
      );
    }

    if (kind === "oral_exam_tips") {
      return (
        <>
          <BsYoutube style={{ marginRight: 6 }} /> ติวสอบปากเปล่า
        </>
      );
    }

    if (kind === "podcast") {
      return (
        <>
          <FaPodcast style={{ marginRight: 6 }} /> Podcast
        </>
      );
    }

    if (kind === "shorts") {
      return (
        <>
          <BsLightningCharge style={{ marginRight: 6 }} /> Shorts
        </>
      );
    }

    if (kind === "shorts_prepare_for_exams") {
      return (
        <>
          <BsLightningCharge style={{ marginRight: 6 }} /> เทคนิคการเรียน
        </>
      );
    }

    return (
      <>
        <BsYoutube style={{ marginRight: 6 }} /> วิดีโอทั้งหมด
      </>
    );
  }, [kind]);

  const setParam = useCallback(
    (next) => {
      const cur = Object.fromEntries([...searchParams.entries()]);
      const merged = { ...cur, ...next };

      // ล้าง key ว่าง/ค่า default
      Object.keys(merged).forEach((k) => {
        if (merged[k] == null || merged[k] === "" || merged[k] === "ทั้งหมด") delete merged[k];
      });

      setSearchParams(merged, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const buildQuery = useCallback(
    ({ startAfterDoc = null } = {}) => {
      const ref = collection(db, "videos");

      const clauses = [where("isActive", "==", true)];

      // kind filter
      if (kind !== "all") clauses.push(where("kind", "==", kind));

      // category filter (exact match)
      if (cat && cat !== "ทั้งหมด") clauses.push(where("categories", "array-contains", cat));

      // sort
      if (sort === "views") {
        clauses.push(orderBy("viewCounts", "desc"));
        clauses.push(orderBy("updatedAt", "desc")); // tie-breaker
      } else {
        clauses.push(orderBy("updatedAt", "desc"));
      }

      clauses.push(limit(pageSize));

      if (startAfterDoc) clauses.push(startAfter(startAfterDoc));

      return query(ref, ...clauses);
    },
    [kind, cat, sort]
  );

  const fetchFirst = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError("");
    exhaustedRef.current = false;
    lastDocRef.current = null;

    try {
      const q = buildQuery();
      const snap = await getDocs(q);

      if (reqIdRef.current !== myReq) return;

      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(rows);

      lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
      if (snap.docs.length < pageSize) exhaustedRef.current = true;
    } catch (e) {
      console.error(e);
      if (reqIdRef.current !== myReq) return;

      // ช่วย debug index
      const msg = e?.message || "โหลดวิดีโอไม่สำเร็จ";
      setError(msg);
      setItems([]);
      exhaustedRef.current = true;
      lastDocRef.current = null;
    } finally {
      if (reqIdRef.current === myReq) setLoading(false);
    }
  }, [buildQuery]);

  const fetchMore = useCallback(async () => {
    if (loadingMore) return;
    if (exhaustedRef.current) return;

    const startDoc = lastDocRef.current;
    if (!startDoc) return;

    setLoadingMore(true);
    setError("");

    try {
      const q = buildQuery({ startAfterDoc: startDoc });
      const snap = await getDocs(q);

      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems((prev) => [...prev, ...rows]);

      lastDocRef.current = snap.docs[snap.docs.length - 1] || startDoc;
      if (snap.docs.length < pageSize) exhaustedRef.current = true;
    } catch (e) {
      console.error(e);
      setError(e?.message || "โหลดเพิ่มเติมไม่สำเร็จ");
      exhaustedRef.current = true;
    } finally {
      setLoadingMore(false);
    }
  }, [buildQuery, loadingMore]);

  // initial & when params change
  useEffect(() => {
    fetchFirst();
  }, [fetchFirst]);

  // keep input in sync
  useEffect(() => {
    setQueryText(qParam);
  }, [qParam]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const term = queryText.trim();
    // NOTE: หน้านี้ไม่ได้ทำ full-text search จริง ๆ
    // แต่รองรับกรอง title/description แบบ client-side เล็ก ๆ เพื่อ UX
    setParam({ q: term || "" });
  };

  // optional client-side keyword filter (เบา ๆ)
  const visibleItems = useMemo(() => {
    const t = (qParam || "").trim().toLowerCase();
    if (!t) return items;

    return items.filter((x) => {
      const title = (x.title || "").toLowerCase();
      const desc = (x.description || "").toLowerCase();
      const cats = Array.isArray(x.categories) ? x.categories.join(" ").toLowerCase() : "";
      return title.includes(t) || desc.includes(t) || cats.includes(t);
    });
  }, [items, qParam]);

  const totalText = useMemo(() => {
    if (loading) return "";
    return `พบ ${visibleItems.length.toLocaleString()} รายการ`;
  }, [visibleItems.length, loading]);

  return (
    <div className="container py-4 videos-page">
      {/* Header */}
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-2">
        <h3 className="mb-0">
          <strong>{titleNode}</strong>
        </h3>

        {/* <div className="d-flex gap-2">
          <Button
            className={sort === "latest" ? "videos-filter-btn is-active" : "videos-filter-btn"}
            size="sm"
            onClick={() => setParam({ sort: "latest" })}
          >
            ล่าสุด
          </Button>

          <Button
            className={sort === "views" ? "videos-filter-btn is-active" : "videos-filter-btn"}
            size="sm"
            onClick={() => setParam({ sort: "views" })}
          >
            ยอดวิว
          </Button>
        </div> */}
      </div>

      {/* Kind Tabs */}
      <div className="videos-kind-scroll mb-3">
        <Button
          size="sm"
          className={kind === "all" ? "videos-pill-btn is-active" : "videos-pill-btn"}
          onClick={() => setParam({ kind: "all" })}
        >
          ทั้งหมด
        </Button>

        <Button
          size="sm"
          className={kind === "appetizer" ? "videos-pill-btn is-active" : "videos-pill-btn"}
          onClick={() => setParam({ kind: "appetizer" })}
        >
          ปูพื้นฐาน
        </Button>

        <Button
          size="sm"
          className={kind === "oral_exam_tips" ? "videos-pill-btn is-active" : "videos-pill-btn"}
          onClick={() => setParam({ kind: "oral_exam_tips" })}
        >
          ติวสอบปากเปล่า
        </Button>

        <Button
          size="sm"
          className={
            kind === "shorts_prepare_for_exams"
              ? "videos-pill-btn is-active"
              : "videos-pill-btn"
          }
          onClick={() => setParam({ kind: "shorts_prepare_for_exams" })}
        >
          เทคนิคการเรียน
        </Button>

        <Button
          size="sm"
          className={kind === "youtube" ? "videos-pill-btn is-active" : "videos-pill-btn"}
          onClick={() => setParam({ kind: "youtube" })}
        >
          YouTube
        </Button>

        <Button
          size="sm"
          className={kind === "podcast" ? "videos-pill-btn is-active" : "videos-pill-btn"}
          onClick={() => setParam({ kind: "podcast" })}
        >
          Podcast
        </Button>
        <Button
          size="sm"
          className={kind === "shorts" ? "videos-pill-btn is-active" : "videos-pill-btn"}
          onClick={() => setParam({ kind: "shorts" })}
        >
          Shorts
        </Button>
      </div>

      {/* Search box (optional) */}
      <form className="mb-3" onSubmit={handleSubmit}>
        <div className="header-search-box" style={{ maxWidth: 520 }}>
          <span className="header-search-icon"><BsSearch /></span>
          <input
            type="search"
            className="form-control header-search-input"
            // placeholder="ค้นหาในหน้านี้ (title/description/categories)"
            placeholder="ค้นหาวิดีโอ"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            enterKeyHint="search"
          />
        </div>
        {qParam ? <div className="small text-secondary mt-2">{totalText}</div> : null}
      </form>

      {/* Category badges */}
      <div className="videos-cat-scroll mb-3">
        {CATEGORY_OPTIONS.map((c) => {
          const active = (cat || "ทั้งหมด") === c;
          return (
            <button
              key={c}
              type="button"
              className={active ? "videos-cat-badge is-active" : "videos-cat-badge"}
              onClick={() => setParam({ cat: c })}
            >
              {c}
            </button>
          );
        })}
      </div>

      {/* Loading / Error / Empty */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center my-4" style={{ minHeight: 160 }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">กำลังโหลด…</span>
          </Spinner>
        </div>
      ) : error ? (
        <div className="text-danger my-3">
          {error}
          <div className="text-secondary small mt-1">
            ถ้าเจอข้อความเกี่ยวกับ index ให้สร้าง composite index ตามที่ Firestore แนะนำได้เลย
          </div>
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="text-secondary">ยังไม่มีรายการ</div>
      ) : (
        <>
          {/* Grid */}
          <div className="row g-3 mt-2">
            {visibleItems.map((v) => (
              <div key={v.id} className="col-12 col-sm-6 col-lg-4">
                <VideoCard v={v} />
              </div>
            ))}
          </div>

          {/* Load more */}
          <div className="d-flex justify-content-center my-4">
            {exhaustedRef.current ? (
              <div className="text-secondary">ไม่มีรายการเพิ่มเติม</div>
            ) : (
              <Button
                className="videos-loadmore-btn"
                onClick={fetchMore}
                disabled={loadingMore}
              >
                {loadingMore ? "กำลังโหลด..." : "โหลดเพิ่มเติม"}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}